// Compile a comparison query from picked (cube, axis) pairs.
//
// The analyst picks two sides and names them; this turns that into SPARQL on the
// Analysis result-shape convention. The naming is the point. "These two axes
// answer the same question" is a human judgment, and an automatic generator that
// inferred it would put an alignment into the Pod that nobody made — which is
// what sank the C3 alignments plan. Here the judgment is the series label, typed
// by the person making it, and it travels with the saved query.
//
// Pure, and in lib/ for the usual reason: tests import it without pulling in
// auth.js or shared/config.js import-time side effects. It also means this file
// cannot reach a Pod even by accident. The prefixes are a parameter rather than
// an import for the same reason.
//
// Nothing here emits a container, a file or a source list. A cube is identified
// by its publisher literal and its dataSet fragment — both of which are carried
// on every observation (lib/aggregate_core.js writes them, and a partner's copy
// is byte-identical, so it carries their publisher and the same fragment). The
// backend still chooses the sources this text runs over; that invariant is not
// weakened by compiling the text.

// A SPARQL string literal. Labels are user-typed and go straight into the query.
const literal = (s) => `"${String(s ?? '')
  .replace(/\\/g, '\\\\')
  .replace(/"/g, '\\"')
  .replace(/\n/g, '\\n')
  .replace(/\r/g, '\\r')
  .replace(/\t/g, '\\t')}"`;

const prefixOf = (curie) => /^([A-Za-z][\w-]*):/.exec(String(curie ?? ''))?.[1] ?? null;

const oxford = (items) =>
  items.length < 2 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

// pairs   — [{ publisher, cubeId, axis, seriesLabel, dimensions[], periods[], measure }]
//           two or more. `dimensions` is what the cube file carries (used for the
//           crossed-cube caveat, never to drive the read); `periods` decides
//           whether the query can carry a period at all.
// measure — fallback measure CURIE for pairs that do not name their own.
// period  — the period predicate CURIE, or null to leave the period out entirely.
// prefixes— { adr: '…#', schema: '…', … }. Only the ones actually used are emitted.
// whole   — true when every side reads the same term with a value scheme: the
//           values then come back as they are, which is how the scheme lists them.
export function buildCompareQuery({ pairs = [], measure = null, period = null, prefixes = {}, whole = false } = {}) {
  if (pairs.length < 2) throw new Error('A comparison needs at least two sides');

  const sides = pairs.map((p, i) => {
    const at = `Side ${i + 1}`;
    if (!p.publisher) throw new Error(`${at} has no publisher`);
    if (!p.cubeId) throw new Error(`${at} has no cube`);
    if (!p.axis) throw new Error(`${at} has no axis`);
    const m = p.measure ?? measure;
    if (!m) throw new Error(`${at} has no measure`);
    return {
      ...p,
      measure: m,
      seriesLabel: p.seriesLabel || `${p.publisher} · ${p.axis}`,
      dimensions: p.dimensions ?? [],
      periods: p.periods ?? [],
    };
  });

  const series = sides.map((s) => s.seriesLabel);
  const duplicate = series.find((label, i) => series.indexOf(label) !== i);
  if (duplicate) {
    // Two blocks with the same name is not a comparison — the panel would draw
    // one heading over two different cubes' numbers.
    throw new Error(`Two sides are both named "${duplicate}" — give each side its own name`);
  }

  // The period is all-or-nothing. An OPTIONAL period binds unbound for the side
  // that lacks one, and the panel would render a blank quarter chip covering
  // half the comparison.
  const withPeriod = Boolean(period) && sides.every((s) => s.periods.length > 0);

  const branches = sides.map((s) => {
    const triples = [
      'a qb:Observation',
      'qb:dataSet ?ds',
      'dcterms:publisher ?publisher',
      `${s.axis} ?v`,
      ...(withPeriod ? [`${period} ?period`] : []),
      `${s.measure} ?n`,
    ];
    return [
      '  {',
      `    ?o ${triples.join(' ;\n       ')} .`,
      `    FILTER(?publisher = ${literal(s.publisher)} && STRENDS(STR(?ds), ${literal(`#${s.cubeId}`)}))`,
      `    BIND(${literal(s.seriesLabel)} AS ?series)`,
      '  }',
    ].join('\n');
  });

  // Every CURIE that made it into the text, plus the two structural vocabularies
  // every branch uses.
  const used = new Set(['qb', 'dcterms']);
  for (const s of sides) {
    for (const c of [s.axis, s.measure, withPeriod ? period : null]) {
      const p = prefixOf(c);
      if (p) used.add(p);
    }
  }
  const header = [...used]
    .filter((p) => prefixes[p])
    .sort()
    .map((p) => `PREFIX ${p}:${' '.repeat(Math.max(1, 8 - p.length))}<${prefixes[p]}>`)
    .join('\n');

  const projected = ['?series', '?publisher', '?value', ...(withPeriod ? ['?period'] : [])];
  const sparql = [
    header,
    '',
    `SELECT ${projected.join(' ')} (SUM(?n) AS ?count) WHERE {`,
    branches.join('\n  UNION\n'),
    '',
    ...(whole
      ? ['  # The values as the term\'s value scheme lists them.',
        '  BIND(?v AS ?value)']
      : ['  # One bridge for both vocabularies: a SKOS concept IRI loses everything up',
        '  # to the fragment, a plain literal passes through as itself.',
        '  BIND(REPLACE(STR(?v), "^.*#", "") AS ?value)']),
    '}',
    `GROUP BY ${projected.join(' ')}`,
    `ORDER BY ?series ${withPeriod ? '?period ' : ''}DESC(?count)`,
  ].join('\n');

  return {
    sparql,
    series,
    description: describe(sides),
    warnings: warn(sides, period, withPeriod),
  };
}

// One sentence: what is compared, and — only when it applies — the undercount
// that a reader of the saved panel has no other way of knowing about. Everything
// else that could be said here is either visible on the builder while authoring
// (the axes, the periods) or in the manual, and a description nobody finishes
// reading protects nobody. The analyst edits it before saving; anything longer
// is theirs to add.
function describe(sides) {
  const label = (s) => `${s.seriesLabel} (${s.cubeId} → ${s.axis})`;
  const list = sides.length === 2
    ? `${label(sides[0])} against ${label(sides[1])}`
    : oxford(sides.map(label));

  const crossed = sides.filter((s) => s.dimensions.length > 1);
  if (!crossed.length) return `${list}.`;

  const which = crossed.length === sides.length
    ? 'each side comes from a cube that counts two things in combination'
    : crossed.length === 1
      ? `"${crossed[0].seriesLabel}" comes from a cube that counts two things in combination`
      : `${oxford(crossed.map((s) => `"${s.seriesLabel}"`))} each come from a cube that counts two things in combination`;
  const effect = crossed.length === 1 ? 'that block reads' : 'those blocks read';

  return `${list} — ${which}, so ${effect} lower than the truth: any combination`
    + ' too small to include was left out of the file, and is missing from these totals.';
}

// Worth saying, not worth blocking on. Each of these produces a query that runs
// and returns something — just something the reader should know the shape of.
function warn(sides, period, withPeriod) {
  const out = [];

  const measures = [...new Set(sides.map((s) => s.measure))];
  if (measures.length > 1) {
    out.push(
      `The sides are counting different things (${measures.join(', ')}). Each block`
      + ' adds up correctly on its own, but the two totals are not the same kind of'
      + ' number, so comparing them directly will mislead.',
    );
  }

  if (period && !withPeriod) {
    const without = sides.filter((s) => !s.periods.length).map((s) => `"${s.seriesLabel}"`);
    out.push(
      `${oxford(without)} is not broken down by period, so period is dropped from`
      + ' the whole comparison — the saved panel will cover all time at once, with'
      + ' no quarter to pick.',
    );
  }

  const covers = sides.map((s) => s.periods.join(','));
  if (withPeriod && new Set(covers).size > 1) {
    out.push(
      'The sides cover different periods. Where one side published nothing for a'
      + ' quarter its block is simply empty — that means "we have no figures", not'
      + ' "we had none", and the panel cannot tell the two apart.',
    );
  }

  const crossed = sides.filter((s) => s.dimensions.length > 1).length;
  if (crossed && crossed !== sides.length) {
    out.push(
      'The two sides are not counted the same way: one counts a single thing, the'
      + ' other counts two things in combination. The combined side leaves out'
      + ' every combination too small to include, so it will tend to read lower'
      + ' than it really is — and the gap is not the difference you are looking'
      + ' for.',
    );
  }

  return out;
}
